import React, { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { useIsResponsive } from '@/hooks'
import { EmtButton, EmtModal, EmtTypography, SendMore } from 'emt-components-v2'
import PetSitterCard from '@/components/PetSitter/PetSitterCard/PetSitterCard'
import { getProfileAsync } from '@/modules/PetSitter/viewProfileServiceHelper'
import { PetsitterAvatar } from '@/components/PetSitter/PetsittersSelection/PetsitterAvatar'
import { EmptyPetsitterSlot } from '@/components/PetSitter/PetsittersSelection/EmptyPetsitterSlot'
import { useNewRequestCartStore } from '@/store/newRequestCartStore'
import { useQueryClient } from '@tanstack/react-query'
import useFloatingProfile from '@/hooks/useFloatingProfile'

interface SendMoreModalProps {
  open: boolean
  setOpen: (open: boolean) => void
  isSubmitLoading?: boolean

  selectedService?: string

  recommendedPetsitters: any[]

  onSend: () => void

  handleOpenProfile: ReturnType<typeof useFloatingProfile>['handleOpenProfile']
}

const checkHasService = (petsitter: any, service?: string) => {
  if (!service) return true

  const priceField =
    service === 'promenade'
      ? 'walkPrice'
      : service === 'visite'
      ? 'homeVisitPrice'
      : service === 'garde-chez-eux'
      ? 'sittingAtPetsitterHomePrice'
      : service === 'garde-chez-vous'
      ? 'sittingAtCustomerHomePrice'
      : undefined

  return priceField ? petsitter[priceField] !== undefined : true
}

const SendMoreModal: React.FC<SendMoreModalProps> = ({
  open,
  setOpen,
  recommendedPetsitters,
  isSubmitLoading = false,
  selectedService,
  onSend,
  handleOpenProfile,
}) => {
  const [isMobile] = useIsResponsive()
  const petsittersIds = useNewRequestCartStore((s) => s.petsittersIds)
  const removePetsitter = useNewRequestCartStore((s) => s.removePetsitter)
  const [isEditing, setIsEditing] = useState(false)
  const [petsitters, setPetsitters] = useState<any[]>([])
  const queryClient = useQueryClient()

  useEffect(() => {
    const fetchPetsitters = async () => {
      if (petsittersIds.length === 0) {
        setPetsitters([])
        return
      }
      try {
        const results = await Promise.all(petsittersIds.map((id) => getProfileAsync(id)))
        setPetsitters(results)
      } catch (error) {
        console.error('Error fetching petsitters:', error)
      }
    }

    fetchPetsitters()
  }, [petsittersIds])

  const handleModifyClick = () => {
    setIsEditing((prev) => !prev)
  }

  const handleRemovePetsitter = (uuid: string) => {
    removePetsitter(uuid)
    queryClient.invalidateQueries(['cartPetsitters'])
  }

  const isLoadingPetsitters = petsittersIds.length > (petsitters?.length || 0)

  return (
    <EmtModal
      open={open}
      onClose={() => setOpen(false)}
      variant="large"
      sx={{
        '& .inner-box': {
          width: '605px',
          maxWidth: '100vw',
          height: isMobile ? '100%' : 'auto',
          maxHeight: '100vh',
          borderRadius: isMobile ? '0' : '24px',
          backgroundColor: '#F9F9F9',
        },
        '& .children-box': { padding: '24px', height: '100%' },
      }}
    >
      <Box className="EmtReactNativeWebViewMarginTop">
        <EmtTypography variant="h4" fontWeight={800} marginBottom="15px">
          Attention, votre demande n&apos;est pas encore envoyée
        </EmtTypography>
        <EmtTypography variant="body1" marginBottom="15px">
          Nous vous conseillons d’ajouter des pet sitters parmi les suivants afin d’avoir plus de réponses (optionnel) :
        </EmtTypography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px',
          }}
        >
          <Box
            sx={{
              maxWidth: '100%',
              overflowY: 'auto',
              minHeight: '0',
              height: isMobile ? 'unset' : '300px',
              maxHeight: '70vh',
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              paddingRight: isMobile ? '0' : '15px',
              marginBottom: isMobile ? '100px' : '170px',
              paddingBottom: isMobile ? '170px' : '50px',
            }}
          >
            {recommendedPetsitters
              // Filter out any petsitter already in selectedContactsInForm
              // .filter((petSitter) => !selectedContactsInForm.some((formSel) => formSel.id === petSitter.id))
              .map((petSitter) => {
                return (
                  <PetSitterCard
                    key={petSitter.id}
                    data={petSitter}
                    select={!petsittersIds.includes(petSitter.uuid)}
                    remove={petsittersIds.includes(petSitter.uuid)}
                    seeProfile
                    selectedService={selectedService}
                    onMobileNavigate={handleOpenProfile}
                  />
                )
              })}
          </Box>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              justifyContent: 'space-between',
              backgroundColor: '#fff',
              position: 'absolute',
              bottom: '0',
              left: '0',
              right: '0',
              padding: '20px',
              borderRadius: '24px',
              boxShadow: '0px -4px 56px 0px #00000014, 0px -12px 16px 0px #00000014',
            }}
            className="EmtReactNativeWebViewPaddingBottom"
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                flexShrink: 0,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <EmtTypography variant="body1" sx={{ fontSize: '18px', fontWeight: 700, lineHeight: '1.1' }}>
                  Votre sélection
                </EmtTypography>
                <EmtTypography
                  variant="body2"
                  sx={{ fontWeight: 700, color: '#A5A3A9', lineHeight: '1.1', flexShrink: 0 }}
                >
                  {petsitters?.filter((petsitter) => checkHasService(petsitter, selectedService)).length ?? 0}/10
                </EmtTypography>
              </Box>
              <EmtTypography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: '#AE59C5',
                  lineHeight: '1.1',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
                onClick={handleModifyClick}
              >
                {isEditing ? 'Enregistrer' : 'Modifier'}
              </EmtTypography>
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                flexWrap: isMobile ? 'nowrap' : 'wrap',
                padding: '0 2px',
                overflowX: isMobile ? 'scroll' : 'auto',
                maxWidth: '100%',
                paddingRight: isMobile ? '57px' : '0',
              }}
            >
              {petsitters?.map((petsitter) => {
                const hasService = checkHasService(petsitter, selectedService)
                return (
                  <PetsitterAvatar
                    key={petsitter.uuid}
                    uuid={petsitter.uuid}
                    name={petsitter.name}
                    mainPicture={petsitter.mainPicture}
                    isEditing={isEditing}
                    hasService={hasService}
                    onRemove={() => handleRemovePetsitter(petsitter.uuid)}
                  />
                )
              })}
              {Array.from({ length: 10 - (petsitters?.length || 0) }).map((_, index) => (
                <EmptyPetsitterSlot
                  key={`empty-${index}`}
                  isLoading={isLoadingPetsitters && index < petsittersIds.length - (petsitters?.length || 0)}
                />
              ))}
            </Box>
            <EmtButton loader={isSubmitLoading} fullWidth size="medium" color="purple" onClick={() => onSend()}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <SendMore width="22px" height="18px" />
                <EmtTypography variant="body2" fontWeight="700">
                  Envoyer votre demande
                </EmtTypography>
              </Box>
            </EmtButton>
          </Box>
        </Box>
      </Box>
    </EmtModal>
  )
}

export default SendMoreModal
